import { INTENTS_ENV } from "@src/utils/environment"
import { useCallback, useMemo, useSyncExternalStore } from "react"

const STORAGE_PREFIX = "defuse:pending-promotion-swap"
const STATE_CHANGED_EVENT = "promotion:pending-swap-changed"

export const PENDING_SWAP_TTL_MS = 48 * 60 * 60 * 1000

export type PendingPromotionSwapScope = {
  promotionId: string
  accountId: string
}

export type PendingPromotionSwap = PendingPromotionSwapScope & {
  settledAt: number
}

function storageKey(scope: PendingPromotionSwapScope): string {
  return [
    STORAGE_PREFIX,
    INTENTS_ENV,
    encodeURIComponent(scope.promotionId),
    encodeURIComponent(scope.accountId),
  ].join(":")
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {}
}

export function markPendingPromotionSwap(
  scope: PendingPromotionSwapScope,
  settledAt = Date.now()
): PendingPromotionSwap | null {
  if (typeof window === "undefined") return null

  const record = { ...scope, settledAt }
  const key = storageKey(scope)
  if (!safeSet(key, JSON.stringify(record))) return null
  notifyStateChanged(key)
  return record
}

export function getPendingPromotionSwap(
  scope: PendingPromotionSwapScope,
  now = Date.now()
): PendingPromotionSwap | null {
  if (typeof window === "undefined") return null

  const key = storageKey(scope)
  const raw = safeGet(key)
  if (raw == null) return null

  try {
    const value: unknown = JSON.parse(raw)
    if (!isPendingPromotionSwap(value, scope)) {
      safeRemove(key)
      return null
    }
    if (now - value.settledAt >= PENDING_SWAP_TTL_MS) {
      safeRemove(key)
      return null
    }
    return value
  } catch {
    safeRemove(key)
    return null
  }
}

export function clearPendingPromotionSwap(
  expected: PendingPromotionSwap
): void {
  if (typeof window === "undefined") return

  const key = storageKey(expected)
  const current = getPendingPromotionSwap(expected)
  if (current?.settledAt !== expected.settledAt) return
  safeRemove(key)
  notifyStateChanged(key)
}

export function usePendingPromotionSwap(
  promotionId: string,
  accountId: string | undefined
): PendingPromotionSwap | null {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (accountId == null) return () => {}
      return onPendingPromotionSwapChanged(
        { promotionId, accountId },
        onStoreChange
      )
    },
    [accountId, promotionId]
  )
  const getSnapshot = useCallback(
    () =>
      accountId == null
        ? null
        : (getPendingPromotionSwap({ promotionId, accountId })?.settledAt ??
          null),
    [accountId, promotionId]
  )

  const settledAt = useSyncExternalStore(subscribe, getSnapshot, () => null)
  return useMemo(
    () =>
      accountId == null || settledAt == null
        ? null
        : { promotionId, accountId, settledAt },
    [accountId, promotionId, settledAt]
  )
}

function isPendingPromotionSwap(
  value: unknown,
  scope: PendingPromotionSwapScope
): value is PendingPromotionSwap {
  return (
    typeof value === "object" &&
    value != null &&
    "promotionId" in value &&
    value.promotionId === scope.promotionId &&
    "accountId" in value &&
    value.accountId === scope.accountId &&
    "settledAt" in value &&
    typeof value.settledAt === "number" &&
    Number.isFinite(value.settledAt) &&
    value.settledAt > 0
  )
}

function notifyStateChanged(key: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent(STATE_CHANGED_EVENT, { detail: { key } })
    )
  } catch {}
}

function onPendingPromotionSwapChanged(
  scope: PendingPromotionSwapScope,
  handler: () => void
): () => void {
  if (typeof window === "undefined") return () => {}

  const key = storageKey(scope)
  let expiryTimer: number | undefined
  const scheduleExpiry = () => {
    if (expiryTimer != null) window.clearTimeout(expiryTimer)
    const record = getPendingPromotionSwap(scope)
    if (record == null) return
    const remaining = record.settledAt + PENDING_SWAP_TTL_MS - Date.now()
    expiryTimer = window.setTimeout(handler, Math.max(0, remaining))
  }
  const refresh = () => {
    handler()
    scheduleExpiry()
  }
  const onCustomEvent = (event: Event) => {
    if (event instanceof CustomEvent && event.detail?.key === key) refresh()
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key === key) refresh()
  }

  scheduleExpiry()
  window.addEventListener(STATE_CHANGED_EVENT, onCustomEvent)
  window.addEventListener("storage", onStorage)
  return () => {
    if (expiryTimer != null) window.clearTimeout(expiryTimer)
    window.removeEventListener(STATE_CHANGED_EVENT, onCustomEvent)
    window.removeEventListener("storage", onStorage)
  }
}
